/**
 * Accessibility utilities for keyboard navigation and screen readers
 */

/**
 * Trap focus within a container (useful for modals/dialogs)
 * @param container - The DOM element to trap focus within
 * @returns Cleanup function to remove the trap
 */
export function trapFocus(container: HTMLElement): () => void {
  const focusableElements = container.querySelectorAll<HTMLElement>(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key !== 'Tab') return;

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    }
  }

  container.addEventListener('keydown', handleKeyDown);
  firstElement?.focus();

  return () => container.removeEventListener('keydown', handleKeyDown);
}

/**
 * Announce a message to screen readers
 * @param message - The message to announce
 * @param priority - 'polite' for non-urgent, 'assertive' for important
 */
export function announce(message: string, priority: 'polite' | 'assertive' = 'polite'): void {
  const announcer = document.getElementById('sr-announcer') || createAnnouncer();
  announcer.setAttribute('aria-live', priority);
  announcer.textContent = '';
  // Small delay ensures screen reader picks up the change
  setTimeout(() => {
    announcer.textContent = message;
  }, 100);
}

function createAnnouncer(): HTMLElement {
  const announcer = document.createElement('div');
  announcer.id = 'sr-announcer';
  announcer.setAttribute('aria-live', 'polite');
  announcer.setAttribute('aria-atomic', 'true');
  announcer.className = 'sr-only';
  document.body.appendChild(announcer);
  return announcer;
}

/**
 * Handle keyboard navigation for lists/grids
 * @param e - Keyboard event
 * @param items - Array of focusable items
 * @param currentIndex - Currently focused index
 * @param onSelect - Callback when item is selected (Enter/Space)
 * @returns New index after navigation
 */
export function handleListKeyboard(
  e: React.KeyboardEvent,
  items: HTMLElement[],
  currentIndex: number,
  onSelect?: (index: number) => void
): number {
  let newIndex = currentIndex;

  switch (e.key) {
    case 'ArrowDown':
    case 'ArrowRight':
      e.preventDefault();
      newIndex = Math.min(currentIndex + 1, items.length - 1);
      break;
    case 'ArrowUp':
    case 'ArrowLeft':
      e.preventDefault();
      newIndex = Math.max(currentIndex - 1, 0);
      break;
    case 'Home':
      e.preventDefault();
      newIndex = 0;
      break;
    case 'End':
      e.preventDefault();
      newIndex = items.length - 1;
      break;
    case 'Enter':
    case ' ':
      e.preventDefault();
      onSelect?.(currentIndex);
      break;
  }

  if (newIndex !== currentIndex) {
    items[newIndex]?.focus();
  }

  return newIndex;
}

/**
 * Generate unique IDs for ARIA relationships
 */
let idCounter = 0;
export function generateId(prefix: string = 'aria'): string {
  return `${prefix}-${++idCounter}`;
}

/**
 * Add a global escape key handler, returns cleanup function
 */
export function handleEscapeKey(e: KeyboardEvent, handler: () => void): void;
export function handleEscapeKey(handler: () => void): () => void;
export function handleEscapeKey(eOrHandler: KeyboardEvent | (() => void), handler?: () => void): (() => void) | void {
  if (typeof eOrHandler === 'function') {
    const listener = (e: KeyboardEvent) => { if (e.key === 'Escape') eOrHandler(); };
    document.addEventListener('keydown', listener);
    return () => document.removeEventListener('keydown', listener);
  } else {
    if (eOrHandler.key === 'Escape') handler?.();
  }
}

/**
 * Get all focusable elements within a container
 */
export function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  );
}
